import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { LoadingState, ConfirmModal, DataTable } from '../../../components/admin/shared';
import { Plus, Edit2, Trash2, Save, X, CheckCircle } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const DocumentTypesConfig = () => {
  const [loading, setLoading] = useState(true);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedService, setSelectedService] = useState('overseas');
  const [editingType, setEditingType] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [success, setSuccess] = useState(false);

  const services = [
    { value: 'overseas', label: 'Overseas Education' },
    { value: 'career', label: 'Career Counseling' },
    { value: 'skills', label: 'Skill Development' },
    { value: 'test-prep', label: 'Test Preparation' },
    { value: 'scholarships', label: 'Scholarship Finder' },
    { value: 'visa', label: 'Visa Assistance' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'pre-departure', label: 'Pre-Departure' },
    { value: 'post-arrival', label: 'Post-Arrival' },
    { value: 'alumni', label: 'Alumni Network' }
  ];

  const availableFileTypes = [
    { value: 'pdf', label: 'PDF' },
    { value: 'jpg', label: 'JPG' },
    { value: 'jpeg', label: 'JPEG' },
    { value: 'png', label: 'PNG' },
    { value: 'doc', label: 'DOC' },
    { value: 'docx', label: 'DOCX' },
    { value: 'xls', label: 'XLS' },
    { value: 'xlsx', label: 'XLSX' }
  ];

  useEffect(() => {
    loadDocumentTypes();
  }, [selectedService]);

  const loadDocumentTypes = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get(`/api/admin/settings/document-types?service=${selectedService}`);
      setDocumentTypes(response.data);
    } catch (error) {
      console.error('Error loading document types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingType({
      id: null,
      service: selectedService,
      documentType: '',
      label: '',
      required: false,
      fileTypes: ['pdf'],
      maxSizeMB: 5,
      description: '',
      active: true,
      order: documentTypes.length + 1
    });
  };

  const handleEdit = (type) => {
    setEditingType({ ...type });
  };

  const handleSave = async () => {
    try {
      if (editingType.id) {
        // Update existing
        await educentialApi.patch(`/api/admin/settings/document-types/${editingType.id}`, editingType);
      } else {
        // Create new
        await educentialApi.post('/api/admin/settings/document-types', editingType);
      }

      setEditingType(null);
      setSuccess(true);
      loadDocumentTypes();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving document type:', error);
      alert('Failed to save document type. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await educentialApi.delete(`/api/admin/settings/document-types/${showDeleteModal.id}`);
      setShowDeleteModal(null);
      setSuccess(true);
      loadDocumentTypes();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting document type:', error);
      alert('Failed to delete document type. Please try again.');
    }
  };

  const handleFileTypeToggle = (fileType) => {
    setEditingType(prev => ({
      ...prev,
      fileTypes: prev.fileTypes.includes(fileType)
        ? prev.fileTypes.filter(t => t !== fileType)
        : [...prev.fileTypes, fileType]
    }));
  };

  const columns = [
    {
      key: 'order',
      label: 'Order',
      render: (type) => <span className="text-sm font-mono">{type.order}</span>
    },
    {
      key: 'documentType',
      label: 'Type Code',
      render: (type) => <span className="font-mono text-sm">{type.documentType}</span>
    },
    {
      key: 'label',
      label: 'Label',
      render: (type) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{type.label}</div>
          {type.required && (
            <span className="text-xs text-red-600">Required</span>
          )}
        </div>
      )
    },
    {
      key: 'fileTypes',
      label: 'Allowed Types',
      render: (type) => (
        <div className="flex flex-wrap gap-1">
          {type.fileTypes.map(ft => (
            <span key={ft} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded uppercase">
              {ft}
            </span>
          ))}
        </div>
      )
    },
    {
      key: 'maxSizeMB',
      label: 'Max Size',
      render: (type) => <span className="text-sm text-gray-600">{type.maxSizeMB} MB</span>
    },
    {
      key: 'active',
      label: 'Active',
      render: (type) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          type.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {type.active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (type) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(type);
            }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(type);
            }}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  if (loading && !editingType) {
    return (
      <AdminLayout title="Document Types">
        <LoadingState message="Loading document types..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Document Types">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Document Types</h2>
            <p className="text-gray-600 mt-1">Manage document type configurations for each service</p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              {services.map(service => (
                <option key={service.value} value={service.value}>
                  {service.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddNew}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Document Type</span>
            </button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-900 font-medium">Document type configuration updated successfully!</p>
          </div>
        )}

        {/* Edit Form */}
        {editingType && (
          <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingType.id ? 'Edit Document Type' : 'Add New Document Type'}
              </h3>
              <button
                onClick={() => setEditingType(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingType.documentType}
                  onChange={(e) => setEditingType({ ...editingType, documentType: e.target.value.toLowerCase() })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 font-mono"
                  placeholder="passport"
                />
                <p className="text-xs text-gray-500 mt-1">Use lowercase with hyphens</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingType.label}
                  onChange={(e) => setEditingType({ ...editingType, label: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  placeholder="Passport Copy"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editingType.description}
                  onChange={(e) => setEditingType({ ...editingType, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  placeholder="Clear copy of passport bio-data page"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum File Size (MB) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={editingType.maxSizeMB}
                  onChange={(e) => setEditingType({ ...editingType, maxSizeMB: parseInt(e.target.value) || 5 })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  min="1"
                  max="100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                <input
                  type="number"
                  value={editingType.order}
                  onChange={(e) => setEditingType({ ...editingType, order: parseInt(e.target.value) || 1 })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  min="1"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed File Types <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {availableFileTypes.map((fileType) => (
                    <label
                      key={fileType.value}
                      className={`flex items-center space-x-2 p-2 border-2 rounded-lg cursor-pointer transition-colors ${
                        editingType.fileTypes.includes(fileType.value)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={editingType.fileTypes.includes(fileType.value)}
                        onChange={() => handleFileTypeToggle(fileType.value)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-gray-900">{fileType.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2 space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingType.required}
                    onChange={(e) => setEditingType({ ...editingType, required: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Required document (must be submitted)</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingType.active}
                    onChange={(e) => setEditingType({ ...editingType, active: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Active (visible in document upload forms)</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
              <button
                onClick={() => setEditingType(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!editingType.documentType || !editingType.label || editingType.fileTypes.length === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>Save Document Type</span>
              </button>
            </div>
          </div>
        )}

        {/* Document Types Table */}
        <DataTable
          columns={columns}
          data={documentTypes}
          loading={loading}
          emptyMessage={`No document types configured for ${services.find(s => s.value === selectedService)?.label}. Click "Add Document Type" to create one.`}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal !== null}
          title="Delete Document Type?"
          message={`Are you sure you want to delete the document type "${showDeleteModal?.label}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          dangerous={true}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(null)}
        />
      </div>
    </AdminLayout>
  );
};

export default DocumentTypesConfig;
